/**
 * カスタムJavaScript
 * プロジェクト全体で使用するJavaScriptを統合
 */

// ==============================================
// カスタム商品選択オプション - 段階的選択UI
// 参考: https://uruotte.com/product/shampoo-noscent/
// ==============================================

class CustomProductOptions {
  constructor(container) {
    this.container = container;
    this.productData = this.getProductData();
    
    if (!this.productData) {
      console.error('商品データが見つかりません');
      return;
    }

    this.sectionId = this.container.dataset.sectionId || '';
    // 数量パターン（ラジオボタン式）が存在するかどうか
    this.hasQuantityOptions = this.container.dataset.hasQuantityOptions === 'true';
    // 全送料ラベルを「送料無料」に強制する（タグ free-shipping で制御）
    this.forceFreeShipping = this.container.dataset.forceFreeShipping === 'true';
    // まとめ買い割引の閾値（数量ベース）。空欄/0/未設定は 0 = まとめ買いなし
    this.bulkDiscountThreshold = this.parseThreshold(this.container.dataset.bulkDiscountThreshold);
    // 送料無料の閾値（数量ベース）。空欄/0/未設定は 0 = 数量による送料無料なし（金額閾値で判定）
    this.freeShippingThreshold = this.parseThreshold(this.container.dataset.freeShippingThreshold);
    // 送料無料になる合計金額の閾値（centで管理: 5,500円 = 550000）
    this.FREE_SHIPPING_THRESHOLD_CENTS = 550000;
    // 通常購入の送料（centで管理: 660円 = 66000）
    this.ONETIME_SHIPPING_CENTS = 66000;
    // 定期コースの送料（centで管理: 220円 = 22000）
    this.SUBSCRIPTION_SHIPPING_CENTS = 22000;

    // 状態管理
    this.currentStep = 1;
    this.selectedVariantId = null;
    this.selectedQuantity = null;
    this.selectedPlan = null;
    this.selectedCycle = null;
    this.selectedVariant = null;
    // 数量パターンなし商品用: 通常数量inputへの参照（init()で設定）
    this.quantityInput = null;

    this.init();
  }

  init() {
    // 数量パターンなし商品用: 通常数量inputへの参照を取得
    this.quantityInput = this.container.querySelector('.quantity-input-number');

    this.setupEventListeners();
    this.initializeSellingPlan();
    this.showStep(1); // 初期状態でステップ1のみ表示
    
    // 初期状態でステップ1のクラスを設定
    const infoContainer = document.querySelector('.product--custom-options .product__info-container');
    if (infoContainer) {
      infoContainer.classList.add('step-1-active');
    }
    
    // 初期表示時にすべてのバリアント価格をフォーマット
    this.formatAllVariantPrices();
    
    // 初期状態でカートに追加ボタンを無効化
    this.updateAddToCartButton();
    // 初期状態でギフトラッピングUIを更新
    this.updateSimpleGiftWrappingVisibility();
  }

  /**
   * 定期購入プランの初期化
   */
  initializeSellingPlan() {
    // 定期購入サイクルの初期化は、ユーザーが定期購入を選択したときに行う
    // 初期状態では何も選択しない
  }

  /**
   * 商品データをJSON要素から取得
   */
  getProductData() {
    const jsonElement = this.container.querySelector('[data-product-data]');
    if (!jsonElement) {
      return null;
    }
    return JSON.parse(jsonElement.textContent);
  }

  /**
   * イベントリスナーの設定
   */
  setupEventListeners() {
    // バリエーション選択（changeとclickの両方を処理）
    const variantInputs = this.container.querySelectorAll('.variant-input');
    variantInputs.forEach(input => {
      input.addEventListener('change', () => this.handleVariantChange());
      // 同じ値を選択した場合でも反応するようにclickイベントも追加
      input.addEventListener('click', (e) => {
        // 現在ステップ1にいて、選択されている場合のみ処理
        if (this.currentStep === 1 && input.checked) {
          // 少し遅延させてchangeイベントが発火したかどうかを確認
          setTimeout(() => {
            // changeイベントが発火しなかった場合（同じ値が選択された場合）も処理
            if (input.checked && this.currentStep === 1) {
              this.handleVariantChange();
            }
          }, 0);
        }
      });
    });

    // 数量選択
    this.setupQuantityEventListeners();

    // 購入プラン選択（changeとclickの両方を処理）
    const planInputs = this.container.querySelectorAll('.plan-input');
    planInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handlePlanChange(e));
      // 同じ値を選択した場合でも反応するようにclickイベントも追加
      input.addEventListener('click', (e) => {
        // 現在ステップ3にいて、選択されている場合のみ処理
        if (this.currentStep === 3 && input.checked) {
          setTimeout(() => {
            if (input.checked && this.currentStep === 3) {
              this.handlePlanChange(e);
            }
          }, 0);
        }
      });
    });

    // 定期購入サイクル選択
    const cycleSelect = this.container.querySelector('.cycle-select');
    if (cycleSelect) {
      cycleSelect.addEventListener('change', (e) => this.handleCycleChange(e));
    }

    // 変更ボタン
    const changeButtons = this.container.querySelectorAll('.step-change-btn');
    changeButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleChange(e));
    });

    // おまとめ購入について / 定期コースについて トグル
    const aboutToggles = this.container.querySelectorAll('.about-toggle-btn');
    aboutToggles.forEach(button => {
      button.addEventListener('click', (e) => this.handleAboutToggle(e));
    });

    // カートに追加ボタン
    const addToCartBtn = this.container.querySelector('.add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  /**
   * 数量選択のイベントリスナーを設定（再描画後も再利用可能）
   */
  setupQuantityEventListeners() {
    const quantityInputs = this.container.querySelectorAll('.quantity-input');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleQuantityChange(e));
      input.addEventListener('click', (e) => {
        if (this.currentStep === 2 && input.checked) {
          setTimeout(() => {
            if (input.checked && this.currentStep === 2) {
              this.handleQuantityChange(e);
            }
          }, 0);
        }
      });
    });
  }

  /**
   * バリエーション変更時の処理
   */
  handleVariantChange() {
    this.updateSelectedVariant();
    // バリアント固有の数量オプション・閾値に更新（再描画含む）
    this.updateVariantSpecificSettings();
    // バリアント選択時は、数量が選択されていなくても価格を更新
    this.updateQuantityPrices();
    // 数量パターンなし商品はバリアント選択時点で数量1として購入プラン価格を即時計算
    if (!this.hasQuantityOptions) {
      this.updatePlanPrices();
    }
    this.updateVariantIdInput();
    // バリアント選択時の価格表示を更新
    this.updateVariantPrices();
    // バリアントキーワード条件付きノートの表示切り替え
    this.updateVariantConditionalNotes();
    // プラン価格ノートの更新
    this.updatePlanPriceNote();
    // ギフトラッピングUIの表示/非表示を更新
    this.updateSimpleGiftWrappingVisibility();
    
    // カートに追加ボタンの状態を更新
    this.updateAddToCartButton();
    
    // バリアント選択後の次ステップへ自動遷移（ステップ1にいる場合のみ）
    if (this.currentStep === 1) {
      this.saveStepSelection(1);
      if (this.hasQuantityOptions) {
        // 数量パターンあり: STEP2（数量選択）へ
        this.goToStep(2);
      } else {
        // 数量パターンなし: STEP2をスキップしてSTEP3（プラン選択）へ
        // STEP3も存在しない場合はSTEP1完了のまま（カートに追加ボタンが有効化される）
        const hasSubscriptionStep = this.container.querySelector('[data-step="3"]');
        if (hasSubscriptionStep && hasSubscriptionStep.querySelector('.plan-input')) {
          this.goToStep(3);
        } else {
          // STEP3もない商品（プランなし・数量パターンなし）: STEP1を完了状態にして終了
          const step1 = this.container.querySelector('[data-step="1"]');
          if (step1) {
            step1.classList.remove('active');
            step1.classList.add('completed');
            const content = step1.querySelector('.option-step-content');
            const changeBtn = step1.querySelector('.step-change-btn');
            const summary = step1.querySelector('.step-selected-summary');
            if (content) content.style.display = 'none';
            if (changeBtn) changeBtn.style.display = 'block';
            if (summary) summary.style.display = 'block';
          }
          this.currentStep = 1;
          const infoContainer = document.querySelector('.product--custom-options .product__info-container');
          if (infoContainer) {
            infoContainer.classList.remove('step-1-active', 'step-2-active', 'step-3-active');
            infoContainer.classList.add('step-1-active');
          }
        }
      }
    }
  }

  /**
   * すべてのバリアント価格をフォーマット（初期表示用）
   */
  formatAllVariantPrices() {
    const variantPriceElements = this.container.querySelectorAll('.variant-price-value[data-variant-price]');
    variantPriceElements.forEach(priceEl => {
      const priceCents = parseInt(priceEl.dataset.variantPrice);
      if (priceCents) {
        const yen = priceCents / 100;
        priceEl.textContent = yen.toLocaleString('ja-JP');
      }
    });
  }

  /**
   * バリアント選択時の価格表示を更新
   */
  updateVariantPrices() {
    if (!this.selectedVariant) return;

    const variantPriceElements = this.container.querySelectorAll('.variant-price-value');
    variantPriceElements.forEach(priceEl => {
      const input = priceEl.closest('.variant-option-item')?.querySelector('.variant-input');
      // variant_price_note が設定されているバリアントはカスタムテキストを維持する
      if (input?.dataset.hasPriceNote) return;
      const variantId = input?.dataset.variantId;
      if (variantId && parseInt(variantId) === this.selectedVariant.id) {
        const yen = this.selectedVariant.price / 100;
        priceEl.textContent = yen.toLocaleString('ja-JP');
      }
    });
  }

  /**
   * バリアントごとのプラン価格ノートを更新
   * variant.plan_price_note（複数行テキストメタフィールド）を [data-plan-price-note] に反映
   */
  updatePlanPriceNote() {
    const noteEl = this.container.querySelector('[data-plan-price-note]');
    if (!noteEl) return;

    const noteRaw = this.selectedVariant?.plan_price_note;
    const note = typeof noteRaw === 'string' ? noteRaw.trim() : '';
    if (note) {
      noteEl.innerHTML = note;
      noteEl.style.display = 'block';
    } else {
      noteEl.innerHTML = '';
      noteEl.style.display = 'none';
    }
  }

  /**
   * data-show-for-variant-keyword 属性を持つ要素をバリアント名で表示切り替え
   * 表示条件: キーワードがバリアント名に含まれる かつ 定期コースが選択されている
   */
  updateVariantConditionalNotes() {
    const notes = this.container.querySelectorAll('[data-show-for-variant-keyword]');
    notes.forEach(note => {
      const keyword = note.dataset.showForVariantKeyword;
      if (!keyword) return;
      const variantTitle = this.selectedVariant
        ? [
            this.selectedVariant.option1,
            this.selectedVariant.option2,
            this.selectedVariant.option3,
            this.selectedVariant.title
          ].filter(Boolean).join(' ')
        : '';
      const variantMatches = variantTitle.includes(keyword);
      const isSubscription = this.selectedPlan === 'subscription';
      note.style.display = (variantMatches && isSubscription) ? 'block' : 'none';
    });
  }

  /**
   * シンプルギフトラッピングUIの表示/非表示を制御
   *
   * 表示条件（すべて満たす場合のみ表示）:
   *   - selectedPlan === 'onetime'（通常購入）
   *   - selectedQuantity が 1〜giftMaxQuantity の範囲
   *   - バリアントタイトルが excludeKeyword に一致しない
   *   - data-gift-variant-id が存在する
   *
   * 非表示時はチェックボックスを必ずOFFにする
   */
  updateSimpleGiftWrappingVisibility() {
    const wrapper = this.container.querySelector('[data-simple-gift-wrapping]');
    if (!wrapper) return;

    const giftVariantId = wrapper.dataset.giftVariantId;
    const giftMaxQty = parseInt(wrapper.dataset.giftMaxQuantity || '2', 10);
    const excludeKeyword = wrapper.dataset.giftExcludeKeyword || '500mL';

    const isOneTime = this.selectedPlan === 'onetime';
    // 数量パターンあり: selectedQuantity / 数量パターンなし: 1固定
    const qty = this.hasQuantityOptions
      ? Number(this.selectedQuantity || 0)
      : 1;
    const isGiftQty = qty >= 1 && qty <= giftMaxQty;

    const variantTitle = [
      this.selectedVariant?.option1,
      this.selectedVariant?.option2,
      this.selectedVariant?.option3,
      this.selectedVariant?.title
    ].filter(Boolean).join(' ');

    const escapedKeyword = excludeKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const isExcluded = new RegExp(escapedKeyword, 'i').test(variantTitle);

    const shouldShow = isOneTime && isGiftQty && !isExcluded && !!giftVariantId;

    wrapper.style.display = shouldShow ? 'block' : 'none';

    if (!shouldShow) {
      const checkbox = wrapper.querySelector('[data-gift-checkbox]');
      if (checkbox) checkbox.checked = false;
    }
  }

  /**
   * 数量ベース閾値をパース。空欄/0以下/数値でない場合は 0（＝機能無効）を返す
   */
  parseThreshold(raw) {
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = parseInt(raw, 10);
    return (isNaN(n) || n <= 0) ? 0 : n;
  }

  /**
   * 指定数量がまとめ買い割引の対象かどうか
   * 閾値が未設定（0）の場合は常に false（まとめ買いなし）
   */
  isBulkQuantity(qty) {
    return this.bulkDiscountThreshold > 0 && qty >= this.bulkDiscountThreshold;
  }

  /**
   * 指定数量・金額が送料無料の対象かどうか
   * 1. 送料無料タグ（forceFreeShipping）→ 常に無料
   * 2. 数量ベース閾値（freeShippingThreshold）→ qty が閾値以上なら無料
   * 3. 合計金額が金額閾値（5,500円）以上なら無料
   */
  isFreeShippingFor(qty, amountCents) {
    if (this.forceFreeShipping) return true;
    if (this.freeShippingThreshold > 0 && qty >= this.freeShippingThreshold) return true;
    return amountCents >= this.FREE_SHIPPING_THRESHOLD_CENTS;
  }

  /**
   * 選択されたバリアントのメタフィールド設定を取得（商品レベルのフォールバック付き）
   * 優先順位: バリアントメタフィールド > 商品メタフィールド > ハードコードされたデフォルト
   */
  getVariantSettings(variant) {
    const parseQtyOptions = (raw) => {
      if (!raw) return null;
      const parsed = String(raw).split(',').map(q => parseInt(q.trim())).filter(q => !isNaN(q) && q > 0);
      return parsed.length > 0 ? parsed : null;
    };

    const defaultQtyOptions =
      parseQtyOptions((this.productData.default_quantity_options || []).join(',')) || [1, 2, 3];
    // 空欄/未設定は 0 = 機能無効（デフォルト2にはしない）
    const defaultBulkThreshold = this.parseThreshold(this.productData.default_bulk_discount_threshold);
    const defaultFreeShippingThreshold = this.parseThreshold(this.productData.default_free_shipping_threshold);

    if (!variant) {
      return {
        quantityOptions: defaultQtyOptions,
        bulkDiscountThreshold: defaultBulkThreshold,
        freeShippingThreshold: defaultFreeShippingThreshold
      };
    }

    const hasValue = (v) => v !== null && v !== undefined && v !== '';
    const quantityOptions = parseQtyOptions(variant.quantity_options) || defaultQtyOptions;
    // バリアントに値があればそれを使用、なければ商品レベルへフォールバック。
    // どちらも空欄なら 0 = 機能無効。
    const bulkDiscountThreshold = hasValue(variant.bulk_discount_threshold)
      ? this.parseThreshold(variant.bulk_discount_threshold)
      : defaultBulkThreshold;
    const freeShippingThreshold = hasValue(variant.free_shipping_threshold)
      ? this.parseThreshold(variant.free_shipping_threshold)
      : defaultFreeShippingThreshold;

    return { quantityOptions, bulkDiscountThreshold, freeShippingThreshold };
  }

  /**
   * 数量オプションをバリアント設定に基づいて再描画
   */
  renderQuantityOptions(quantityOptions, bulkDiscountThreshold) {
    const container = this.container.querySelector('.quantity-options');
    if (!container) return;

    const html = quantityOptions.map(qty => {
      const isBulk = this.isBulkQuantity(qty);
      const baseAmount = this.selectedVariant ? this.selectedVariant.price * qty : 0;
      const isFreeShipping = this.isFreeShippingFor(qty, baseAmount);
      return `
        <div class="quantity-option-item">
          <input
            type="radio"
            id="quantity-${qty}-${this.sectionId}"
            name="quantity-option"
            value="${qty}"
            class="quantity-input"
            data-is-bulk="${isBulk}"
          >
          <label for="quantity-${qty}-${this.sectionId}" class="quantity-option-label">
            <span class="quantity-label-wrapper">
              <span class="quantity-number">${qty}点</span>
              ${isBulk ? '<span class="bulk-discount-badge">まとめ買い割引適用</span>' : ''}
            </span>
            <span class="quantity-price-wrapper">
              <span class="quantity-shipping-label">${isFreeShipping ? '送料無料' : '送料別 660円'}</span>
            </span>
          </label>
        </div>`;
    }).join('');

    container.innerHTML = html;
    this.setupQuantityEventListeners();

    if (this.selectedVariant) {
      this.updateQuantityPrices();
    }
  }

  /**
   * バリアント変更時に数量オプション・閾値をバリアント固有の設定に更新
   */
  updateVariantSpecificSettings() {
    if (!this.selectedVariant) return;

    const settings = this.getVariantSettings(this.selectedVariant);

    this.bulkDiscountThreshold = settings.bulkDiscountThreshold;
    this.freeShippingThreshold = settings.freeShippingThreshold;

    const currentOptions = Array.from(
      this.container.querySelectorAll('.quantity-input')
    ).map(i => parseInt(i.value));

    const optionsChanged =
      currentOptions.length !== settings.quantityOptions.length ||
      settings.quantityOptions.some((qty, i) => qty !== currentOptions[i]);

    if (optionsChanged) {
      this.selectedQuantity = null;
      this.updateQuantityInput();
      this.renderQuantityOptions(settings.quantityOptions, settings.bulkDiscountThreshold);
    }
  }

  /**
   * 選択されたバリアントを更新
   */
  updateSelectedVariant() {
    const selectedOptions = {};
    const variantInputs = this.container.querySelectorAll('.variant-input:checked');
    
    variantInputs.forEach(input => {
      const position = input.dataset.optionPosition;
      selectedOptions[`option${position}`] = input.value;
    });

    // マッチするバリアントを検索
    this.selectedVariant = this.productData.variants.find(variant => {
      return Object.keys(selectedOptions).every(key => {
        return variant[key] === selectedOptions[key];
      });
    });

    if (!this.selectedVariant) {
      this.selectedVariant = this.productData.variants[0];
    }

    this.selectedVariantId = this.selectedVariant.id;
  }

  /**
   * 数量変更時の処理
   */
  handleQuantityChange(e) {
    this.selectedQuantity = parseInt(e.target.value);
    this.updateAllPrices();
    this.updateQuantityInput();
    // ギフトラッピングUIの表示/非表示を更新
    this.updateSimpleGiftWrappingVisibility();
    
    // カートに追加ボタンの状態を更新
    this.updateAddToCartButton();
    
    // 数量選択後、自動的に次のステップへ
    // ただし、現在ステップ2にいる場合のみ
    if (this.currentStep === 2) {
      this.saveStepSelection(2);
      
      // 定期購入が有効な場合はステップ3へ、無効な場合は完了
      const hasSubscription = this.container.querySelector('[data-step="3"]');
      if (hasSubscription && hasSubscription.querySelector('.plan-input')) {
        this.goToStep(3);
      } else {
        // 定期購入がない場合は、ステップ2を完了状態にして終了
        const step2 = this.container.querySelector('[data-step="2"]');
        if (step2) {
          step2.classList.add('completed');
          const changeBtn = step2.querySelector('.step-change-btn');
          const summary = step2.querySelector('.step-selected-summary');
          if (changeBtn) changeBtn.style.display = 'block';
          if (summary) summary.style.display = 'block';
        }
      }
    }
  }

  /**
   * 購入プラン変更時の処理
   */
  handlePlanChange(e) {
    this.selectedPlan = e.target.value;
    
    // 定期購入が選択された場合、サイクル選択を表示
    const cycleWrapper = this.container.querySelector('[data-subscription-options]');
    if (cycleWrapper) {
      if (this.selectedPlan === 'subscription') {
        cycleWrapper.style.display = 'block';
        // 定期購入の場合、最初のサイクルを自動選択
        const cycleSelect = this.container.querySelector('.cycle-select');
        if (cycleSelect && cycleSelect.options.length > 0) {
          this.selectedCycle = cycleSelect.value || cycleSelect.options[0].value;
          // サイクル選択後、価格を更新（選択されたSelling Planに基づく）
          this.updatePlanPrices();
          // サイクル選択後、ステップ3を完了状態にする（現在ステップ3にいる場合のみ）
          if (this.currentStep === 3) {
            const step3 = this.container.querySelector('[data-step="3"]');
            if (step3) {
              step3.classList.add('completed');
              // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
            }
          }
        }
      } else {
        cycleWrapper.style.display = 'none';
        this.selectedCycle = null;
        // 通常購入の場合、即座にステップ3を完了状態にする（現在ステップ3にいる場合のみ）
        if (this.currentStep === 3) {
          const step3 = this.container.querySelector('[data-step="3"]');
          if (step3) {
            step3.classList.add('completed');
            // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
          }
        }
      }
    } else {
      // cycleWrapperがない場合（定期購入なし）は即座に完了（現在ステップ3にいる場合のみ）
      if (this.currentStep === 3) {
        const step3 = this.container.querySelector('[data-step="3"]');
        if (step3) {
          step3.classList.add('completed');
          // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
        }
      }
    }
    
    this.updateAllPrices();
    this.updateSellingPlanInput();
    // プラン変更に応じて注意文言の表示/非表示を更新
    this.updateVariantConditionalNotes();
    // ギフトラッピングUIの表示/非表示を更新
    this.updateSimpleGiftWrappingVisibility();
    
    // カートに追加ボタンの状態を更新
    this.updateAddToCartButton();
  }

  /**
   * 定期購入サイクル変更時の処理
   */
  handleCycleChange(e) {
    this.selectedCycle = e.target.value;
    this.updateSellingPlanInput();
    // サイクル変更時は購入プランの価格を更新（選択されたSelling Planに基づく）
    this.updatePlanPrices();
    
    // カートに追加ボタンの状態を更新
    this.updateAddToCartButton();
    
    // サイクル選択後、ステップ3を完了状態にする（現在ステップ3にいる場合のみ）
    if (this.currentStep === 3) {
      const step3 = this.container.querySelector('[data-step="3"]');
      if (step3) {
        step3.classList.add('completed');
        // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
      }
    }
  }

  /**
   * 変更ボタンのクリック処理
   */
  handleChange(e) {
    const step = parseInt(e.target.dataset.changeStep);
    
    // ステップ1に戻る場合は、選択状態をリセット
    if (step === 1) {
      this.resetStep1Selection();
    }
    // ステップ2に戻る場合は、数量選択をリセット
    else if (step === 2) {
      this.resetStep2Selection();
    }
    // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
    
    this.goToStep(step);
  }
  
  /**
   * ステップ1の選択をリセット
   */
  resetStep1Selection() {
    const variantInputs = this.container.querySelectorAll('.variant-input');
    variantInputs.forEach(input => {
      input.checked = false;
    });
    this.selectedVariant = null;
    this.selectedVariantId = null;
    this.selectedQuantity = null;
    this.selectedPlan = null;
    this.selectedCycle = null;
    this.updateSellingPlanInput();
    this.updateVariantConditionalNotes();
    this.updateSimpleGiftWrappingVisibility();
  }
  
  /**
   * ステップ2の選択をリセット
   */
  resetStep2Selection() {
    const quantityInputs = this.container.querySelectorAll('.quantity-input');
    quantityInputs.forEach(input => {
      input.checked = false;
    });
    this.selectedQuantity = null;
    this.selectedPlan = null;
    this.selectedCycle = null;
    this.updateSellingPlanInput();
    this.updateVariantConditionalNotes();
    this.updateSimpleGiftWrappingVisibility();
  }
  

  /**
   * 特定のステップに移動
   */
  goToStep(step) {
    // すべてのステップを閉じる
    const allSteps = this.container.querySelectorAll('.product-option-step');
    allSteps.forEach((stepEl, index) => {
      const stepNum = parseInt(stepEl.dataset.step);
      stepEl.classList.remove('active');
      
      const content = stepEl.querySelector('.option-step-content');
      const changeBtn = stepEl.querySelector('.step-change-btn');
      const summary = stepEl.querySelector('.step-selected-summary');
      
      if (content) content.style.display = 'none';
      if (changeBtn) changeBtn.style.display = 'none';
      if (summary) summary.style.display = 'none';
      
      // 完了したステップにはcompletedクラスを追加
      if (stepNum < step) {
        stepEl.classList.add('completed');
        // ステップ3の変更ボタンは削除済み（最後のステップなので変更不可）
        if (stepNum !== 3 && changeBtn) changeBtn.style.display = 'block';
        if (summary) summary.style.display = 'block';
      } else {
        stepEl.classList.remove('completed');
      }
      
      // 未到達のステップにはdisabledクラスを追加
      if (stepNum > step) {
        stepEl.classList.add('disabled');
      } else {
        stepEl.classList.remove('disabled');
      }
    });

    // .product__info-containerにクラスを追加して背景色を変更
    const infoContainer = document.querySelector('.product--custom-options .product__info-container');
    if (infoContainer) {
      // 既存のステップクラスを削除
      infoContainer.classList.remove('step-1-active', 'step-2-active', 'step-3-active');
      // 現在のステップのクラスを追加
      infoContainer.classList.add(`step-${step}-active`);
    }

    // 指定されたステップを開く
    this.showStep(step);
    this.currentStep = step;
  }

  /**
   * ステップを表示
   */
  showStep(step) {
    const stepElement = this.container.querySelector(`[data-step="${step}"]`);
    if (!stepElement) return;

    stepElement.classList.add('active');
    const content = stepElement.querySelector('.option-step-content');
    const changeBtn = stepElement.querySelector('.step-change-btn');

    if (content) content.style.display = 'block';
    if (changeBtn) changeBtn.style.display = 'none';

    // ステップ2が表示されたとき、バリアントが選択されていれば価格を更新
    if (step === 2 && this.selectedVariant) {
      this.updateQuantityPrices();
    }
    // 数量パターンなし商品でSTEP3が表示されたとき: 数量1で価格を計算
    else if (step === 3 && this.selectedVariant && !this.hasQuantityOptions) {
      this.updatePlanPrices();
    }
    // 数量パターンあり商品: 数量が選択済みの場合のみ更新
    else if (this.selectedVariant && this.selectedQuantity) {
      this.updateAllPrices();
    }
  }

  /**
   * 「について」トグルの処理
   */
  handleAboutToggle(e) {
    const button = e.currentTarget;
    const section = button.closest('.quantity-about-section, .subscription-about-section');
    const content = section.querySelector('.about-content');
    const icon = button.querySelector('.toggle-icon');

    if (content.style.display === 'none' || !content.style.display) {
      content.style.display = 'block';
      if (icon) icon.style.transform = 'rotate(180deg)';
    } else {
      content.style.display = 'none';
      if (icon) icon.style.transform = 'rotate(0deg)';
    }
  }

  /**
   * 数量オプションのバッジ・送料ラベルを更新
   */
  updateQuantityPrices() {
    if (!this.selectedVariant) return;

    this.container.querySelectorAll('.quantity-option-item').forEach(item => {
      const input = item.querySelector('.quantity-input');
      if (!input) return;
      const qty = parseInt(input.value);
      if (!qty) return;

      const isBulk = this.isBulkQuantity(qty);
      const baseAmount = this.selectedVariant.price * qty;
      const isFreeShipping = this.isFreeShippingFor(qty, baseAmount);

      // data-is-bulk を最新の閾値で同期
      input.dataset.isBulk = isBulk;

      // まとめ買いバッジの表示切り替え
      const labelWrapper = item.querySelector('.quantity-label-wrapper');
      if (labelWrapper) {
        const existingBadge = labelWrapper.querySelector('.bulk-discount-badge');
        if (isBulk && !existingBadge) {
          const badge = document.createElement('span');
          badge.className = 'bulk-discount-badge';
          badge.textContent = 'まとめ買い割引適用';
          labelWrapper.appendChild(badge);
        } else if (!isBulk && existingBadge) {
          existingBadge.remove();
        }
      }

      // 送料ラベル
      const shippingLabelEl = item.querySelector('.quantity-shipping-label');
      if (shippingLabelEl) {
        shippingLabelEl.textContent = isFreeShipping ? '送料無料' : '送料別 660円';
        shippingLabelEl.classList.toggle('is-free-shipping', isFreeShipping);
      }
    });
  }

  /**
   * すべての価格を更新
   */
  updateAllPrices() {
    if (!this.selectedVariant) return;

    // 数量オプションの価格を更新
    this.updateQuantityPrices();

    // 購入プランの価格を更新
    // 数量パターンあり: selectedQuantity が選択済みの場合のみ
    // 数量パターンなし: 数量1固定なので常に更新
    if (this.selectedQuantity || !this.hasQuantityOptions) {
      this.updatePlanPrices();
    }
  }

  /**
   * 選択されたSelling Planの価格調整を取得
   */
  getSellingPlanPriceAdjustment(variantId, sellingPlanId) {
    if (!this.productData || !this.productData.variants) return null;

    const variant = this.productData.variants.find(v => v.id === variantId);
    if (!variant || !variant.selling_plan_allocations) return null;

    const allocation = variant.selling_plan_allocations.find(
      a => a.selling_plan_id === parseInt(sellingPlanId)
    );
    if (!allocation || !allocation.price_adjustments || allocation.price_adjustments.length === 0) {
      return null;
    }

    // 最初の価格調整を取得（通常は1回目の注文に対する調整）
    const adjustment = allocation.price_adjustments.find(a => !a.order_count || a.order_count === 1) || allocation.price_adjustments[0];
    return adjustment;
  }

  /**
   * Selling Planに基づいて価格を計算
   * allocation.price（Shopify事前計算済み単価）を優先して使用
   */
  calculatePriceWithSellingPlan(basePrice, quantity, sellingPlanId) {
    if (!sellingPlanId || !this.selectedVariantId) {
      return basePrice * quantity;
    }

    const variant = this.productData.variants.find(v => v.id === this.selectedVariantId);
    if (!variant || !variant.selling_plan_allocations) {
      return basePrice * quantity;
    }

    const allocation = variant.selling_plan_allocations.find(
      a => a.selling_plan_id === parseInt(sellingPlanId)
    );
    if (!allocation) {
      return basePrice * quantity;
    }

    // allocation.price はShopifyが事前計算した割引済み単価（最も正確）
    if (allocation.price !== null && allocation.price !== undefined) {
      return allocation.price * quantity;
    }

    // フォールバック: price_adjustments から計算
    const adjustment = this.getSellingPlanPriceAdjustment(this.selectedVariantId, sellingPlanId);
    if (!adjustment) {
      return basePrice * quantity;
    }

    let adjustedPrice = basePrice * quantity;

    if (adjustment.value_type === 'percentage') {
      adjustedPrice = Math.round(adjustedPrice * (1 - adjustment.value / 100));
    } else if (adjustment.value_type === 'fixed_amount') {
      adjustedPrice = Math.max(0, adjustedPrice - adjustment.value);
    } else if (adjustment.value_type === 'price') {
      adjustedPrice = adjustment.value * quantity;
    }

    return adjustedPrice;
  }

  /**
   * 購入プランの価格を更新
   * 通常購入: まとめ買い対象なら10%オフ価格を表示
   * 定期コース: Selling Plan の割引を適用（まとめ買い割引とは別扱い）
   */
  updatePlanPrices() {
    if (!this.selectedVariant) return;
    // 数量パターンあり: selectedQuantity が未選択なら計算しない
    if (this.hasQuantityOptions && !this.selectedQuantity) return;

    // 数量パターンなし商品は1固定
    const effectiveQuantity = this.hasQuantityOptions ? this.selectedQuantity : 1;

    const basePrice = this.selectedVariant.price;
    const isBulk = this.isBulkQuantity(effectiveQuantity);

    // 通常購入: まとめ買いは10%オフ
    const onetimeTotal = isBulk
      ? Math.round(basePrice * effectiveQuantity * 0.9)
      : basePrice * effectiveQuantity;

    // 通常購入: 送料無料タグ OR 数量閾値 OR 合計金額が閾値以上なら送料無料
    const onetimeFreeShipping = this.isFreeShippingFor(effectiveQuantity, onetimeTotal);
    const onetimeShippingCost = onetimeFreeShipping ? 0 : this.ONETIME_SHIPPING_CENTS;
    const onetimeGrandTotal = onetimeTotal + onetimeShippingCost;

    // 通常購入価格
    const onetimePriceEl = this.container.querySelector('[data-plan="onetime"]');
    if (onetimePriceEl) {
      onetimePriceEl.textContent = this.formatPrice(onetimeTotal);
    }

    // 通常購入の送料表示
    const onetimePlanItem = this.container.querySelector('[data-plan="onetime"]')?.closest('.purchase-plan-item');
    if (onetimePlanItem) {
      const onetimeShippingEl = onetimePlanItem.querySelector('.plan-shipping');
      if (onetimeShippingEl) {
        onetimeShippingEl.textContent = onetimeFreeShipping ? '送料無料' : '+送料 660円';
      }
    }

    // 通常購入: 合計金額表示
    const onetimeGrandTotalEl = this.container.querySelector('[data-plan-grand-total="onetime"]');
    if (onetimeGrandTotalEl) {
      onetimeGrandTotalEl.textContent = `合計 ${this.formatPrice(onetimeGrandTotal)}`;
    }

    // 通常購入: 1点あたり価格（送料込み・2点以上の場合のみ表示）
    const onetimePerUnitEl = this.container.querySelector('[data-plan-per-unit="onetime"]');
    if (onetimePerUnitEl) {
      if (effectiveQuantity >= 2) {
        const perUnit = Math.round(onetimeGrandTotal / effectiveQuantity);
        onetimePerUnitEl.textContent = `1点あたり ${this.formatPrice(perUnit)}`;
        onetimePerUnitEl.style.display = 'block';
      } else {
        onetimePerUnitEl.style.display = 'none';
      }
    }

    // 定期購入価格（選択されたSelling Planに基づいて計算）
    const subscriptionPriceEl = this.container.querySelector('[data-plan="subscription"]');
    if (subscriptionPriceEl && this.productData.has_subscription) {
      const sellingPlanId = this.selectedCycle || this.getFirstSellingPlanId();
      const subscriptionTotal = this.calculatePriceWithSellingPlan(basePrice, effectiveQuantity, sellingPlanId);
      subscriptionPriceEl.textContent = this.formatPrice(subscriptionTotal);

      // 定期購入: 送料無料タグ OR 数量閾値 OR 定期合計金額が閾値以上なら送料無料
      const subscriptionFreeShipping = this.isFreeShippingFor(effectiveQuantity, subscriptionTotal);
      const subscriptionShippingCost = subscriptionFreeShipping ? 0 : this.SUBSCRIPTION_SHIPPING_CENTS;
      const subscriptionGrandTotal = subscriptionTotal + subscriptionShippingCost;

      const subscriptionPlanItem = subscriptionPriceEl.closest('.purchase-plan-item');
      if (subscriptionPlanItem) {
        const subscriptionShippingEl = subscriptionPlanItem.querySelector('.plan-shipping');
        if (subscriptionShippingEl) {
          subscriptionShippingEl.textContent = subscriptionFreeShipping ? '送料無料' : '+送料 220円';
        }
      }

      // 定期購入: 合計金額表示
      const subscriptionGrandTotalEl = this.container.querySelector('[data-plan-grand-total="subscription"]');
      if (subscriptionGrandTotalEl) {
        subscriptionGrandTotalEl.textContent = `合計 ${this.formatPrice(subscriptionGrandTotal)}`;
      }

      // 定期購入: 1点あたり価格（送料込み・2点以上の場合のみ表示）
      const subscriptionPerUnitEl = this.container.querySelector('[data-plan-per-unit="subscription"]');
      if (subscriptionPerUnitEl) {
        if (effectiveQuantity >= 2) {
          const perUnit = Math.round(subscriptionGrandTotal / effectiveQuantity);
          subscriptionPerUnitEl.textContent = `1点あたり ${this.formatPrice(perUnit)}`;
          subscriptionPerUnitEl.style.display = 'block';
        } else {
          subscriptionPerUnitEl.style.display = 'none';
        }
      }
    }
  }

  /**
   * 最初のSelling Plan IDを取得
   * バリアントのAllocationデータを優先（DOMのselectより確実）
   */
  getFirstSellingPlanId() {
    if (
      this.selectedVariant &&
      this.selectedVariant.selling_plan_allocations &&
      this.selectedVariant.selling_plan_allocations.length > 0
    ) {
      return this.selectedVariant.selling_plan_allocations[0].selling_plan_id;
    }
    const cycleSelect = this.container.querySelector('.cycle-select');
    if (cycleSelect && cycleSelect.options.length > 0) {
      const firstOption = cycleSelect.options[0];
      if (firstOption.value) {
        return firstOption.value;
      }
    }
    return null;
  }

  /**
   * 価格をフォーマット
   */
  formatPrice(cents) {
    const yen = cents / 100;
    return `${yen.toLocaleString('ja-JP')}円`;
  }

  /**
   * Hidden inputのバリアントIDを更新
   */
  updateVariantIdInput() {
    const variantIdInput = this.container.querySelector('.product-variant-id');
    if (variantIdInput && this.selectedVariantId) {
      variantIdInput.value = this.selectedVariantId;
    }
  }

  /**
   * ステップの選択内容を保存・表示
   */
  saveStepSelection(stepNum) {
    if (stepNum === 1) {
      // バリアント選択の要約を表示
      const summary = this.container.querySelector('[data-summary-step="1"]');
      if (summary && this.selectedVariant) {
        const nameEl = summary.querySelector('.selected-variant-name');
        const priceEl = summary.querySelector('.selected-variant-price');
        
        if (nameEl) nameEl.textContent = this.selectedVariant.title || this.productData.title;
        if (priceEl) priceEl.textContent = `通常価格 ${this.formatPrice(this.selectedVariant.price)}`;
      }
    } else if (stepNum === 2) {
      // 数量選択の要約を表示（数量パターンあり商品のみSTEP2が存在する）
      const summary = this.container.querySelector('[data-summary-step="2"]');
      if (summary) {
        const textEl = summary.querySelector('.selected-quantity-text');
        const shippingEl = summary.querySelector('.selected-quantity-shipping');

        const basePrice = this.selectedVariant.price;
        const isBulk = this.isBulkQuantity(this.selectedQuantity);
        const totalPrice = isBulk
          ? Math.round(basePrice * this.selectedQuantity * 0.9)
          : basePrice * this.selectedQuantity;

        if (textEl) {
          const label = isBulk ? 'まとめ買い' : '通常価格';
          textEl.textContent = `${this.selectedQuantity}点　${label} ${this.formatPrice(totalPrice)}`;
        }
        if (shippingEl) {
          const summaryFreeShipping = this.isFreeShippingFor(this.selectedQuantity, totalPrice);
          shippingEl.textContent = summaryFreeShipping ? '送料無料' : '送料660円';
        }
      }
    }
  }

  /**
   * Hidden inputの数量を更新
   */
  updateQuantityInput() {
    const quantityInput = this.container.querySelector('.product-quantity');
    if (quantityInput) {
      quantityInput.value = this.selectedQuantity;
    }
  }

  /**
   * Selling Plan (定期購入プラン) の更新
   */
  updateSellingPlanInput() {
    const sellingPlanInput = this.container.querySelector('.product-selling-plan');
    const subscriptionIdInput = this.container.querySelector('.product-subscription-id');
    
    if (this.selectedPlan === 'subscription' && this.selectedCycle) {
      // Selling Plan IDをセット
      if (sellingPlanInput) {
        sellingPlanInput.value = this.selectedCycle;
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = this.selectedCycle;
      }
    } else {
      // 通常購入の場合はクリア
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = '';
      }
    }
  }

  /**
   * カートに追加ボタンの有効/無効を更新
   */
  updateAddToCartButton() {
    const addToCartBtn = this.container.querySelector('.add-to-cart-btn');
    if (!addToCartBtn) return;

    // 全ての必須ステップが選択されているかチェック
    const hasVariant = this.selectedVariant !== null;
    // 数量パターンあり: selectedQuantity が選択済みであること
    // 数量パターンなし: STEP2自体が存在しないため数量は1固定 → 常にtrue
    const hasQuantity = this.hasQuantityOptions
      ? this.selectedQuantity !== null
      : true;
    
    // ステップ3のチェック（定期購入が有効な場合）
    const hasSubscription = this.container.querySelector('[data-step="3"]');
    let hasPlan = true;
    
    if (hasSubscription && hasSubscription.querySelector('.plan-input')) {
      // 定期購入が有効な場合、購入プランが選択されている必要がある
      hasPlan = this.selectedPlan !== null;
      
      // 定期購入が選択されている場合、サイクルも選択されている必要がある
      if (this.selectedPlan === 'subscription') {
        hasPlan = hasPlan && this.selectedCycle !== null;
      }
    }

    // 全ての条件を満たしている場合のみ有効化
    const isEnabled = hasVariant && hasQuantity && hasPlan;
    addToCartBtn.disabled = !isEnabled;
  }

  /**
   * カートに追加
   *
   * 処理順:
   *  1. バリアント・在庫確認
   *  2. 数量確認（数量パターンあり: selectedQuantity / なし: quantityInput）
   *  3. selectedPlan 確認
   *  4. selling_plan 整理
   *  5. カート取得 → 通常/定期 混在チェック
   *  6. NGならエラー表示して return
   *  7. items 構築（ギフトラッピング含む）
   *  8. /cart/add.js へ POST
   */
  async handleAddToCart() {
    // 1. バリアント・在庫確認
    if (!this.selectedVariant || !this.selectedVariant.available) {
      this.showNotification('申し訳ございません。この商品は現在在庫切れです。', 'error');
      return;
    }

    // 2. 数量確認
    // 数量パターンあり: selectedQuantity を使用（未選択はエラー）
    // 数量パターンなし: STEP2なしで数量1固定
    if (this.hasQuantityOptions && !this.selectedQuantity) {
      this.showNotification('数量を選択してください。', 'error');
      return;
    }
    const quantity = this.hasQuantityOptions
      ? Number(this.selectedQuantity)
      : 1;

    // 3. プラン確認（STEP3が存在する場合）
    if (this.container.querySelector('[data-step="3"] .plan-input') && !this.selectedPlan) {
      this.showNotification('購入プランを選択してください。', 'error');
      return;
    }

    const addToCartBtn = this.container.querySelector('.add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = '追加中...';
    }

    try {
      // 4. selling_plan 整理
      const sellingPlanId = (this.selectedPlan === 'subscription' && this.selectedCycle)
        ? this.selectedCycle
        : null;

      // 5. items 構築
      const items = [{
        id: this.selectedVariantId,
        quantity: quantity
      }];

      if (sellingPlanId) {
        items[0].selling_plan = sellingPlanId;
      }

      // ギフトラッピング追加（通常購入時のみ・チェックON時のみ・絶対に定期には追加しない）
      if (!sellingPlanId) {
        const giftWrapper = this.container.querySelector('[data-simple-gift-wrapping]');
        if (giftWrapper) {
          const giftCheckbox = giftWrapper.querySelector('[data-gift-checkbox]');
          const giftVariantId = giftWrapper.dataset.giftVariantId;
          if (giftCheckbox && giftCheckbox.checked && giftVariantId) {
            items.push({
              id: parseInt(giftVariantId, 10),
              quantity: 1,
              properties: { '_gift_wrapping': 'true' }
            });
          }
        }
      }

      // 8. /cart/add.js へ POST（sections付きでバッジ・通知UIを同時更新）
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: this._getCartSections(),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        // Shopify（およびチェックアウト検証アプリ）が返す理由を拾って表示する。
        // 本文が読めない場合のみ従来の汎用文言にフォールバックする。
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.description || errorData?.message || 'カートへの追加に失敗しました'
        );
      }

      const data = await response.json();

      // カートUIを更新（バッジ・通知）してカスタムイベントを発火
      this.updateCartUI(data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

    } catch (error) {
      console.error('カート追加エラー:', error);
      this.showNotification(
        error?.message || 'カートへの追加中にエラーが発生しました',
        'error'
      );
    } finally {
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'カートに追加';
      }
    }
  }

  /**
   * /cart/add.js レスポンスに含まれるセクションリストを返す
   */
  _getCartSections() {
    const sections = ['cart-icon-bubble'];
    if (document.querySelector('cart-notification')) {
      sections.push('cart-notification-product', 'cart-notification-button');
    }
    if (document.querySelector('cart-drawer')) {
      sections.push('cart-drawer');
    }
    return sections;
  }

  /**
   * /cart/add.js レスポンスを使ってカートアイコンバッジ・通知UIを更新する
   */
  updateCartUI(parsedState) {
    const sections = parsedState.sections;

    // カートアイコンバッジを更新
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && sections?.['cart-icon-bubble']) {
      const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const content = parsed.querySelector('.shopify-section');
      if (content) {
        cartIconBubble.innerHTML = content.innerHTML;
      }
    }

    // テーマ標準の cart-notification を使って通知表示
    const cartNotification = document.querySelector('cart-notification');
    const itemKey = parsedState.items?.[0]?.key ?? parsedState.key;
    if (cartNotification && itemKey && sections) {
      cartNotification.renderContents({ key: itemKey, sections });
      return;
    }

    // cart-drawer がある場合は renderContents で開く
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sections?.['cart-drawer']) {
      cartDrawer.renderContents(parsedState);
      return;
    }

    // フォールバック：カスタムトースト通知
    this.showNotification('カートに追加しました', 'success');
  }

  /**
   * 通知を表示
   */
  showNotification(message, type = 'info') {
    // 簡易的な通知（テーマの通知システムがあればそれを使用）
    console.log(`[${type}] ${message}`);
    
    // カスタム通知を表示する場合
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 16px 24px;
      background: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : '#2196f3'};
      color: white;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

/**
 * カスタム商品オプションの初期化
 */
function initCustomProductOptions() {
  const containers = document.querySelectorAll('.custom-product-options');
  containers.forEach(container => {
    new CustomProductOptions(container);
  });
}

// ==============================================
// 初期化処理
// ==============================================

// DOMContentLoaded時またはすぐに初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCustomProductOptions();
  });
} else {
  initCustomProductOptions();
}

// Shopify Section Renderingのサポート
document.addEventListener('shopify:section:load', () => {
  initCustomProductOptions();
});

// ==============================================
// 会員情報登録フォーム（AL CustomerMetaSync）
// 姓名カナを1行2列で表示する
// ==============================================

// カナ入力欄のメタフィールド（namespace.key）
const KANA_METAFIELD_KEYS = {
  lastName: 'custom.last_name_kana',
  firstName: 'custom.first_name_kana',
};

/**
 * カナ入力欄（姓・名）の .alcms-field を取得する
 */
function findKanaFields() {
  const findInput = (key) =>
    document.querySelector(`input[name="metafield[${key}]"]`);

  const lastNameInput = findInput(KANA_METAFIELD_KEYS.lastName);
  const firstNameInput = findInput(KANA_METAFIELD_KEYS.firstName);
  if (!lastNameInput || !firstNameInput) return null;

  const lastNameField = lastNameInput.closest('.alcms-field');
  const firstNameField = firstNameInput.closest('.alcms-field');
  if (!lastNameField || !firstNameField) return null;

  return { lastNameField, firstNameField };
}

/**
 * カナ入力欄をグリッド用のラッパーでまとめる
 * @returns {boolean} まとめられた場合はtrue
 */
function wrapKanaFields() {
  const fields = findKanaFields();
  if (!fields) return false;

  const { lastNameField, firstNameField } = fields;
  const parent = lastNameField.parentElement;
  if (!parent || firstNameField.parentElement !== parent) return false;
  if (parent.classList.contains('alcms-field-group--kana')) return true;

  const wrapper = document.createElement('div');
  wrapper.className = 'alcms-field-group alcms-field-group--kana';
  parent.insertBefore(wrapper, lastNameField);
  wrapper.appendChild(lastNameField);
  wrapper.appendChild(firstNameField);

  return true;
}

/**
 * カナ入力欄のレイアウト調整を初期化する
 * フォームが遅れて描画されるケースに備えてDOMの変化も監視する
 */
function initKanaFieldLayout() {
  if (wrapKanaFields()) return;

  const observer = new MutationObserver(() => {
    if (wrapKanaFields()) {
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // フォームが存在しないページで監視し続けないよう一定時間で打ち切る
  setTimeout(() => observer.disconnect(), 10000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKanaFieldLayout);
} else {
  initKanaFieldLayout();
}

